import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import KanbanColumn from './components/KanbanColumn';
import FilterBar from './components/FilterBar';
import ContextMenu from './components/ContextMenu';
import JobDetailsModal from './components/JobDetailsModal';
import KanbanCardSkeleton from '../../components/ui/KanbanCardSkeleton';
import Button from '../../components/ui/Button';
import { maintenanceJobsService } from '../../services/supabaseService';
import toast from 'react-hot-toast';

const MaintenanceKanbanWorkflowBoard = () => {
  const [jobs, setJobs] = useState([]);
  const [draggedJob, setDraggedJob] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, position: { x: 0, y: 0 }, job: null });
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    property: '',
    contractor: '',
    trade: '',
    priority: '',
    dateFrom: '',
    dateTo: ''
  });

  const columns = [
  { id: 'new', title: 'New', description: 'Newly reported jobs' },
  { id: 'assigned', title: 'Assigned', description: 'Contractor assigned' },
  { id: 'in_progress', title: 'In Progress', description: 'Work underway' },
  { id: 'awaiting_invoice', title: 'Awaiting Invoice', description: 'Work completed' },
  { id: 'completed', title: 'Completed', description: 'Fully closed' }];


  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await maintenanceJobsService?.getAll();
      
      // Enrich data with property address
      const enrichedData = data?.map(job => ({
        ...job,
        propertyAddress: job?.properties ? `${job?.properties?.address1}, ${job?.properties?.postcode}` : 'N/A',
        hasAttachments: job?.attachmentsUrls && job?.attachmentsUrls?.length > 0,
        attachmentCount: job?.attachmentsUrls ? job?.attachmentsUrls?.length : 0
      }));
      
      setJobs(enrichedData);
    } catch (error) {
      console.error('Error fetching maintenance jobs:', error);
      toast?.error('Failed to load maintenance jobs');
    } finally {
      setLoading(false);
    }
  };

  const propertyOptions = [];
  const contractorOptions = [];
  const tradeOptions = [
  { value: 'Plumbing', label: 'Plumbing' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Heating', label: 'Heating' },
  { value: 'Carpentry', label: 'Carpentry' },
  { value: 'Painting', label: 'Painting' },
  { value: 'Roofing', label: 'Roofing' },
  { value: 'Glazing', label: 'Glazing' },
  { value: 'General', label: 'General Maintenance' }];


  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e?.preventDefault();
    if (draggedJob) {
      try {
        // Update in database
        await maintenanceJobsService?.update(draggedJob?.id, { status: newStatus });
        
        // Update local state
        setJobs((prevJobs) =>
          prevJobs?.map((job) =>
            job?.id === draggedJob?.id ? { ...job, status: newStatus } : job
          )
        );
        toast?.success('Job status updated');
      } catch (error) {
        console.error('Error updating job status:', error);
        toast?.error('Failed to update job status');
      }
      setDraggedJob(null);
    }
  };

  const handleContextMenu = (e, job) => {
    e?.preventDefault();
    setContextMenu({
      show: true,
      position: { x: e?.clientX, y: e?.clientY },
      job
    });
  };

  const handleContextAction = (action, job) => {
    switch (action) {
      case 'view':case 'edit':
        setSelectedJob(job);
        setShowJobModal(true);
        break;
      case 'reassign':console.log('Reassign contractor for', job?.id);
        break;
      case 'priority':console.log('Change priority for', job?.id);
        break;
      case 'invoice':console.log('Add invoice for', job?.id);
        break;
      case 'attach':console.log('Attach files to', job?.id);
        break;
      case 'note':console.log('Add note to', job?.id);
        break;
      case 'delete':
        handleDeleteJob(job?.id);
        break;
      default:
        break;
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await maintenanceJobsService?.delete(jobId);
      setJobs((prevJobs) => prevJobs?.filter((j) => j?.id !== jobId));
      toast?.success('Job deleted successfully');
    } catch (error) {
      console.error('Error deleting job:', error);
      toast?.error('Failed to delete job');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      property: '',
      contractor: '',
      trade: '',
      priority: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleSaveJob = async (updatedJob) => {
    try {
      await maintenanceJobsService?.update(updatedJob?.id, updatedJob);
      setJobs((prevJobs) =>
        prevJobs?.map((job) => job?.id === updatedJob?.id ? updatedJob : job)
      );
      toast?.success('Job updated successfully');
    } catch (error) {
      console.error('Error updating job:', error);
      toast?.error('Failed to update job');
    }
  };

  const getFilteredJobs = (status) => {
    return jobs?.filter((job) => {
      const matchesStatus = job?.status === status;
      const matchesSearch = !filters?.search ||
      job?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      job?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      job?.jobId?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesProperty = !filters?.property || job?.propertyId === filters?.property;
      const matchesContractor = !filters?.contractor || job?.assignedContractor === filters?.contractor;
      const matchesTrade = !filters?.trade || job?.trade === filters?.trade;
      const matchesPriority = !filters?.priority || job?.priority === filters?.priority;

      return matchesStatus && matchesSearch && matchesProperty && matchesContractor && matchesTrade && matchesPriority;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <SessionManager />
        <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-[1920px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 font-heading">
                  Maintenance Workflow
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Drag and drop jobs between columns to update status
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {columns?.map((column, index) => (
                <div key={column?.id} className="bg-surface rounded-lg border border-border p-4">
                  <div className="mb-4">
                    <div className="h-6 w-32 bg-muted rounded animate-shimmer" />
                    <div className="h-4 w-24 bg-muted/70 rounded mt-2 animate-shimmer" />
                  </div>
                  <KanbanCardSkeleton count={3} />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SessionManager />
      <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-[1920px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 font-heading">
                Maintenance Workflow
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Drag and drop jobs between columns to update status
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left">
                
                Export
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left">
                
                New Job
              </Button>
            </div>
          </div>

          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            propertyOptions={propertyOptions}
            contractorOptions={contractorOptions}
            tradeOptions={tradeOptions} />
          

          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns?.map((column) =>
            <KanbanColumn
              key={column?.id}
              column={column}
              jobs={getFilteredJobs(column?.id)}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onContextMenu={handleContextMenu} />

            )}
          </div>
        </div>
      </main>
      {contextMenu?.show &&
      <ContextMenu
        position={contextMenu?.position}
        job={contextMenu?.job}
        onClose={() => setContextMenu({ show: false, position: { x: 0, y: 0 }, job: null })}
        onAction={handleContextAction} />

      }
      <JobDetailsModal
        job={selectedJob}
        isOpen={showJobModal}
        onClose={() => {
          setShowJobModal(false);
          setSelectedJob(null);
        }}
        onSave={handleSaveJob} />
      
    </div>);

};

export default MaintenanceKanbanWorkflowBoard;